// Pure rules for the digital print designer barrel: its query string, which
// layouts an image allows, and the config shape the fiber embeds take
// (CLIENT_INTEGRATION.md in bitreel-fiber-product-viewer).

export const VIEWER_PREFIX = 'bitreel-digital-print';
export const EDITOR_PREFIX = 'bitreel-digital-print-editor';

export const QUERY = Object.freeze({
  material: 'material',
  opacity: 'opacity',
  layout: 'layout',
  panels: 'panels',
  props: 'props',
  placement: 'placement',
  // Read only: links from the gallery and the standalone configurator.
  imageLink: 'image_link',
  orientation: 'orientation',
});

export const DEFAULT_MATERIAL = 'resin';
export const DEFAULT_OPACITY = 4;
export const DEFAULT_PANELS = 2;

// The size the standalone configurator opens library images at.
export const EMBED_IMAGE_FIT = 2000;

const PLACEMENT_KEYS = ['posX', 'posY', 'scaleX', 'scaleY', 'rotation'];
// Rounded so a shared link renders the frame it was made from.
const PLACEMENT_DECIMALS = 4;

const IMAGE_NUMBER_IN_LINK = /3FORM-IM(\d{5})/i;
const FIT_SEGMENT = /fit=\d+x\d+/;

export const imageNumberFromLink = link => {
  const digits = (link || '').match(IMAGE_NUMBER_IN_LINK)?.[1];
  return digits ? `IM${digits}` : null;
};

export const urlAtFit = (url, size) =>
  (url || '').replace(FIT_SEGMENT, `fit=${size}x${size}`);

const round = value => Number(value.toFixed(PLACEMENT_DECIMALS));

// "0.0123,-0.05,1.2,1,15" in the URL; null when absent or malformed.
export const parsePlacement = value => {
  if (!value) return null;
  const numbers = value.split(',').map(Number);
  if (numbers.length !== PLACEMENT_KEYS.length) return null;
  if (numbers.some(number => !Number.isFinite(number))) return null;
  return Object.fromEntries(
    PLACEMENT_KEYS.map((key, index) => [key, numbers[index]])
  );
};

export const formatPlacement = placement => {
  if (!placement) return null;
  const numbers = PLACEMENT_KEYS.map(key => Number(placement[key]));
  if (numbers.some(number => !Number.isFinite(number))) return null;
  return numbers.map(round).join(',');
};

// Multi-panel runs are for murals: library images Libris scales that way,
// and any upload.
export const layoutsFor = (designer, image) => {
  const layouts = designer.layouts || [];
  const allowsMulti =
    image?.kind === 'upload' || image?.scale === designer.mural_scale;
  return layouts.filter(
    layout => layout.orientation !== 'multi' || allowsMulti
  );
};

// Library images open in their own orientation; uploads open portrait, as
// in the standalone configurator.
export const defaultOrientationFor = image =>
  (image?.orientation || '').toLowerCase() === 'landscape'
    ? 'landscape'
    : 'portrait';

export const opacityLevelFrom = (levels, value) => {
  const text = (value || '').toUpperCase();
  return levels.find(
    level => level.label === text || String(level.level) === text
  );
};

// What each embed is sent for the image.
export const embedImageFor = image => {
  if (!image) return null;
  if (image.kind === 'upload') return { blob: image.blob, name: image.name };
  return { url: urlAtFit(image.src, EMBED_IMAGE_FIT) };
};

export const layoutConfigFor = ({ orientation, panels }) =>
  orientation === 'multi' ? { orientation, panels } : { orientation };
